// Conversation with the assistant — the app's home page.
export default {
    title: 'Assistant',
    today: 'Today',
    yesterday: 'Yesterday',
    previousDay: 'Previous day with a conversation',
    nextDay: 'Next day with a conversation',
    backToToday: 'Back to today',
    emptyDay: 'Nothing was said that day.',
    loadFailed: 'Couldn’t load the conversation.',
    retry: 'Try again',
    composer: {
        placeholder: 'Tell me what you do, eat, spend or plan…',
        send: 'Send',
        speak: 'Speak',
        stop: 'Finish and send',
        cancel: 'Cancel recording',
        receipt: 'Add photos',
        addPhoto: 'Add another photo',
        removePhoto: 'Remove the photo',
        photoHint: 'Add what to do with it — or send it as it is. | Add what to do with them — or send them as they are.',
        photoLimit: 'At most {n} photos in one message — I added the first {n}.',
        readingPhotos: 'Reading {n} photo… | Reading {n} photos — this may take about a minute…',
        listening: 'Listening…',
        transcribing: 'Recognising…',
        thinking: 'The assistant is writing it down…',
        thinkingNamed: '{name} is writing it down…',
        photoSent: {
            expense: 'Receipt photo',
            meal: 'Meal photo',
            shopping: 'Shopping list photo',
            task: 'To-do list photo',
            project: 'Project list photo',
            event: 'Event photo',
            note: 'Note photo',
            unknown: 'Photo',
        },
        photosSent: '{n} photo | {n} photos',
    },
    goingOn: {
        title: 'Going on',
        finish: 'Finish',
        finishText: 'Finished: {name}',
    },
    rightNow: {
        title: 'Right now',
        more: '+{n} more',
        done: 'Done',
        now: 'now',
        in: 'in {time}',
        at: 'at {time}',
        late: '{time} late',
        going: 'on now · until {time}',
        today: 'today',
        payLate: 'overdue',
        projectToday: 'due today',
        projectTomorrow: 'due tomorrow',
        overdue: '{n} overdue task | {n} overdue tasks',
        minutes: '{m} min',
        hours: '{h} h',
        hoursMinutes: '{h} h {m} min',
        personDate: '{what}: {name}',
        inDays: 'in {n} day | in {n} days',
        gifts: '{n} gift idea | {n} gift ideas',
        lastContact: 'last {when}',
        noContact: 'no contact yet',
        care: '{subject}: {name}',
        careDone: 'Done',
        warranty: 'Warranty ends: {name}',
        petBirthday: 'Birthday: {name}',
        petAge: 'turns {n} | turns {n}',
        habits: 'Habits today: {done} of {total}',
        medicationTaken: 'Taken',
        medicationStock: 'Running out: {name}',
        medicationDaysLeft: '{n} day left | {n} days left',
    },
    questions: {
        title: 'To complete',
        answer: 'Answer',
        dismiss: 'Skip the question',
    },
    message: {
        failed: 'Couldn’t send.',
        aiUnavailable: 'The assistant is unavailable right now.',
        aiLimit: 'AI limit used up — the message was not sent.',
        retry: 'Send again',
        discard: 'Remove',
        delete: 'Delete message',
        play: 'Read out loud',
        spoken: 'Spoken',
    },
    record: {
        undo: 'Undo',
        undone: 'undone',
        missing: 'deleted',
        deleted: 'deleted',
        open: 'View',
        undoFailed: 'Couldn’t undo.',
    },
    remove: {
        title: 'Delete the message?',
        withRecords: 'Also delete what was saved from it',
        confirm: 'Delete',
        cancel: 'Cancel',
        failed: 'Couldn’t delete the message.',
    },
    summary: {
        open: 'Sum up the day',
        disabledHint: 'No messages on this day yet — tell the assistant what you do and there will be something to sum up.',
        title: 'Day entry',
        intro: 'The assistant put the day into a few sentences. Change what you like and save.',
        loading: 'The assistant is writing a summary…',
        empty: 'This day has no conversation to sum up yet.',
        save: 'Save the day entry',
        cancel: 'Cancel',
        saved: 'Day entry saved.',
        failed: 'Couldn’t prepare the summary.',
        saveFailed: 'Couldn’t save the day entry.',
    },
    sheet: {
        open: 'Cheat sheet',
        openHint: 'What you can say and where it goes',
        title: 'Cheat sheet',
        intro: 'Talk or type naturally, like to someone close — several things at once is fine. Click an example to paste it into the field.',
        notEnabled: 'turns on with the first entry',
        suggestionsTitle: 'For example',
        groups: {
            wallet: {
                title: 'Spending and income',
                where: 'Wallet — a transaction with a category. Then you can send a photo of the receipt with the camera next to the field: it splits the expense into products.',
                ex1: 'Spent 30 at the grocery store',
                ex2: 'Paid 15 for a taxi',
                ex3: 'Got 100 back from Kate',
            },
            food2: {
                title: 'Food',
                where: 'Food — a meal with calories and macros, eaten or planned.',
                ex1: 'Had oatmeal with a banana',
                ex2: 'Lunch was tomato soup and two dumplings',
                ex3: 'Planning pasta with pesto for dinner',
            },
            basket: {
                title: 'Things to buy',
                where: 'Groceries — items on the shopping list.',
                ex1: 'Buy milk, eggs and bread',
                ex2: 'We’re out of toothpaste',
            },
            forma: {
                title: 'Exercise and sleep',
                where: 'Forma — a workout or a night of sleep.',
                ex1: 'Ran for 30 minutes',
                ex2: 'Going to bed',
                ex3: 'Woke up at 7',
            },
            plans: {
                title: 'Plans',
                where: 'Planner — an event when you give a time, a task when you don’t (without a day it goes to “To schedule”).',
                ex1: 'Dentist tomorrow at 6 pm',
                ex2: 'Need to call mom on Friday',
                ex3: 'Should change the tires some day',
            },
            activities: {
                title: 'Activities',
                where: 'Planner — an activity with a start and an end. The assistant times it; ongoing ones show above the field with a “Finish” button.',
                ex1: 'Taking a shower',
                ex2: 'Done',
                ex3: 'Cleaned the flat from 2 to 3 pm',
            },
            notes: {
                title: 'Notes and thoughts',
                where: 'Notebook — when you ask to note something; name a category and the note goes there. A plain thought or story about the day stays in the conversation and goes into the day summary.',
                ex1: 'Note: gift idea for Anna — a book about mountains',
                ex2: 'Note in books: Solaris',
                ex3: 'Today was really calm',
            },
            projects: {
                title: 'Projects',
                where: 'Projects — tasks in the project with that name, in a section if you name one. When there is no such project, the assistant asks which one.',
                ex1: 'Add to renovation: paint, rollers, tape',
                ex2: 'To renovation, in the kitchen: replace the tap',
            },
            people: {
                title: 'People',
                where: 'People — birthdays and other important dates, gift ideas and talks, on the person I recognise by name or by “mum”, “boss”. A new person is added on the way.',
                ex1: 'Kate’s birthday is on 14 March',
                ex2: 'Gift idea for mum: a new frying pan',
                ex3: 'I talked to dad about the holidays today',
            },
            pets: {
                title: 'Pets',
                where: 'Pets — vaccinations, deworming and other care (done moves the next date) and weight. A new pet is added on the way.',
                ex1: 'I had Rex vaccinated against rabies today, 120',
                ex2: 'Rex weighs 12.4 kg',
                ex3: 'When is Rex due for deworming?',
            },
            devices: {
                title: 'Devices',
                where: 'Devices — services, inspections and replacements with their cost (done moves the next date) and the warranty. A new device is added on the way.',
                ex1: 'I changed the filter in the air purifier',
                ex2: 'The car had its inspection today, 150',
                ex3: 'The washing machine is under warranty until 12 May 2028',
            },
            habits: {
                title: 'Habits',
                where: 'Habits — I tick off the habit I recognise by its name ("water" → Water), and for a daily goal I add as much as you say. I add a new habit only when you ask for one.',
                ex1: 'I drank 2 glasses of water',
                ex2: 'No sweets today',
                ex3: 'How am I doing with water?',
            },
            medications: {
                title: 'Medications',
                where: 'Medications — "I took my meds" ticks off the doses whose time has just passed or is coming up (and today’s late ones), "I took paracetamol" the nearest dose of that medication. A medication not in Medications is saved as a one-off intake.',
                ex1: 'I took my meds',
                ex2: 'I’m not taking vitamin D today',
                ex3: 'Did I take my meds today?',
            },
            photos: {
                title: 'Photos',
                where: 'The camera next to the field — up to 10 photos at once (you can also paste or drag them). A receipt, a list, a note, a poster or a plate — the assistant recognises it and saves it; a long list over a few photos becomes one. Add a word to say where. When it does not know, it asks.',
                ex1: 'A photo of a list + “to shopping”',
                ex2: 'A photo of a page + “tasks for tomorrow”',
                ex3: 'A photo of a concert poster',
            },
            questions: {
                title: 'Questions about your data',
                where: 'Answered from what is saved — this and last week, and plans two weeks ahead.',
                ex1: 'How much did I spend this week?',
                ex2: 'What do I have tomorrow?',
                ex3: 'When did I last go running?',
                ex4: 'What now?',
            },
            corrections: {
                title: 'Corrections',
                where: 'Corrects the last saved thing. Every message also has “Undo”.',
                ex1: 'Not 30, 35',
                ex2: 'That was yesterday',
                ex3: 'Undo that',
            },
        },
    },
    help: {
        welcome: { title: 'Talking to the assistant', text: 'Here you say or type during the day what you do, eat, spend and plan. The assistant saves it in the right modules at once and answers briefly.' },
        composer: { title: 'Message field', text: 'Write naturally, several things at once too. Enter sends, Shift+Enter is a new line.' },
        mic: { title: 'Microphone', text: 'Press, speak, send. When you speak, the assistant answers by voice too (change it in Settings → App).' },
        receipt: { title: 'Photos', text: 'Up to 10 at once: a receipt, a shopping or to-do list, a note, a poster, a plate of food. The assistant recognises what is on them and saves it in the right place (a few photos of one list make one list) — add a word if you want it elsewhere. When it is not sure, it asks. A receipt goes to the last expense (or the one it asked about).' },
        records: { title: 'What was saved', text: 'Under a message you see what was saved from it and where. “See” opens the module, “Undo” removes it. Struck through — undone; with a dash — already deleted in the module.' },
        rightNow: { title: 'Right now', text: 'What needs your attention soon: today’s events, a task within 3 hours, a late task, a meal to eat, a payment due today, a project deadline, overdue tasks, and from People — birthdays and other important dates within a week, and people you haven’t been in touch with for a while, and from Pets and Devices — care a week before its date (and after it until done), a warranty ending within a month and a pet’s birthday, and from Medications — a dose from half an hour before its time (if not ticked off, until the end of the day) and a medication whose stock lasts less than a week. Most pressing on top. The circle ticks a task off, “Done” saves the care, “Taken” ticks off a dose, a click opens the module. You can also ask “what now?”.' },
        goingOn: { title: 'Ongoing', text: 'Activities you started that haven’t ended yet, with the time so far. “Finish” or a plain “done” saves the end. When something runs over 3 hours, the assistant asks on its own.' },
        questions: { title: 'To fill in', text: 'What the assistant asked about and you haven’t answered yet — e.g. a receipt or what was in the basket. Answer with a normal message or close the question with the cross.' },
        days: { title: 'Days of the conversation', text: 'It’s one conversation, seen day by day. Use the arrows to go to earlier days.' },
        summary: { title: 'Sum up the day', text: 'The assistant gathers the day in a few sentences. Edit and save it as the day entry in the Planner.' },
        done: { title: 'Help is here', text: 'The cheat sheet with examples, this guide and First steps — always under “Help”.' },
    },
    firstSteps: {
        expense: { title: 'First expense', action: 'Say an expense', what: 'Say how much and where you spent, e.g. “spent 30 at the grocery store”.', why: 'It goes to the Wallet with a category — no form to open.' },
        receipt: { title: 'Receipt for an expense', action: 'Send a photo', what: 'After an expense, take a photo of the receipt with the camera next to the field.', why: 'The assistant splits the expense into products and categories, and the photo stays with the transaction.' },
        meal: { title: 'A meal', action: 'Say what you eat', what: 'Say what you ate or plan to eat.', why: 'Food counts calories and macros, and the day in the Planner shows the meal.' },
        activity: { title: 'An activity from start to end', action: 'Start an activity', what: 'Say “taking a shower”, then “done”.', why: 'The assistant times it and puts it on the day’s timeline in the Planner.' },
        plan: { title: 'A plan', action: 'Say a plan', what: 'Say what’s ahead, e.g. “dentist tomorrow at 6 pm” or “need to call mom”.', why: 'With a time it becomes an event, without one — a task in the Planner.' },
        question: { title: 'A question about your data', action: 'Ask', what: 'Ask e.g. “how much did I spend this week?” or “what do I have tomorrow?”.', why: 'No searching through modules — the assistant does the math and answers.' },
        voice: { title: 'Say it out loud', action: 'Turn on the mic', what: 'Press the microphone and say anything from this list.', why: 'Fastest on the go — and the assistant answers by voice too.' },
    },
    introduction: {
        title: 'Let’s get to know each other',
        open: 'Introductions',
        openHint: 'Names, voice and goals — once more',
        hello: 'Hi! I’m your assistant. Tell me or type during the day what you do, eat, spend and plan — I’ll save it in the right places. Before we start, let’s get to know each other.',
        skip: 'Skip',
        skipAll: 'Skip all',
        skipped: 'Skipped',
        next: 'Next',
        back: 'Back',
        save: 'Save',
        start: 'Let’s start',
        stepOf: 'Step {n} of {total}',
        phases: {
            hello: { label: 'Welcome', hint: 'Meet your assistant', title: 'Hi! I’m your assistant.', lead: 'I can talk with you, help you plan and save things in the right places in Rountine.' },
            you: { label: 'Your name', hint: 'What should I call you?', title: 'Now you.', lead: 'Tell me what to call you — I’ll use your name sparingly.' },
            goals: { label: 'Your goals', hint: 'What matters to you?', title: 'What matters to you?', lead: 'We can set your goals now or later — you can skip any section.' },
            areas: { label: 'Areas of life', hint: 'Choose where we start', title: 'Where do we start?', lead: 'I’ll pin the areas you pick to the bar so they’re at hand. Everything else stays in the menu.' },
            end: { label: 'Done', hint: 'Let’s get going!', title: 'All set!', lead: 'Everything is saved. You can come back to the introductions any time.' },
        },
        hints: {
            assistantName: 'Pick one of the suggestions or write your own.',
            voice: 'You can change the voice later in Settings.',
            userName: 'You can skip it too — I’ll just talk without a name.',
            goalsPick: 'You can skip any section along the way.',
            nutrition: 'Worked out with the Mifflin-St Jeor formula — you can adjust it.',
            areas: 'You can change the pins in Settings → Menu.',
        },
        assistantName: {
            ask: 'What should I be called? Give me a name — e.g. Ollie or Sam.',
            placeholder: 'e.g. Ollie',
            answered: '{name} — I like it!',
            female: 'Ollie',
            male: 'Sam',
            beFemale: 'You can be {name}',
            beMale: 'Call yourself {name}',
            surpriseMe: 'Surprise me',
            own: 'I’ll type it myself',
            surprise: {
                female: 'Emma, Lily, Mia, Zoe, Ivy, Ruby',
                male: 'Max, Leo, Finn, Theo, Jack, Noah',
            },
        },
        voice: {
            ask: 'Which voice should I use? Click to listen.',
        },
        userName: {
            ask: 'And what’s your name? What should I call you?',
            placeholder: 'e.g. Paul',
            answered: 'Nice to meet you, {name}!',
        },
        gender: {
            ask: 'How should I address you — as a woman or a man?',
            female: 'As a woman',
            male: 'As a man',
        },
        goalsAsk: {
            ask: 'Want to set your goals now? Target weight, nutrition goals and a savings goal — it takes a moment.',
            yes: 'Yes, let’s do it',
            no: 'Not now',
        },
        areas: {
            ask: 'Which areas of life shall we start with? I’ll pin them to the bar — the rest is in the menu.',
            food: 'Food',
            health: 'Health and fitness',
            money: 'Money',
            home: 'Home and loved ones',
            work: 'Work and ideas',
        },
        goalsLater: 'Sure. You can set your goals any time:',
        later: {
            weight: 'in Forma, on the Weight tab',
            nutrition: 'in Settings → Goals',
            savings: 'in the Wallet, under Plans',
        },
        goalsPick: {
            ask: 'Which goals shall we set?',
        },
        goals: {
            weight: 'Target weight',
            nutrition: 'Nutrition goals',
            savings: 'Savings goal',
        },
        weight: {
            ask: 'How much do you weigh now, and what weight are you aiming for?',
            now: 'Now',
            target: 'Target',
            answer: 'target {kg} kg',
        },
        nutrition: {
            ask: 'I’ll work out your daily calories and macros. I need a few details:',
            activity: 'Activity',
        },
        nutritionResult: {
            ask: 'That’s {calories} kcal a day: protein {protein} g, carbs {carbs} g, fat {fat} g. Save it? You can adjust the calories.',
        },
        savingsExisting: {
            ask: 'You already have savings goals: {list}. Add another one?',
            add: 'Add another',
        },
        savings: {
            ask: 'What are you saving for? Give it a name, an amount and a deadline.',
            name: 'For',
            namePlaceholder: 'e.g. Holidays',
            amount: 'Amount',
            deadline: 'By',
            noDeadline: 'No deadline',
            monthly: 'Monthly',
            account: 'On account',
            initial: 'Already saved',
            newAccount: 'I’ll create a “{name}” account in the Wallet with what you’ve already saved.',
        },
        end: 'All set! Now just tell me what’s going on — I’ll take care of the rest.',
        returnHint: 'You can come back to the introductions any time: “Help” in the conversation or Settings → Profile.',
        again: 'Get to know each other again',
        closedHint: 'You can come back to the introductions from “Help” in the conversation or Settings → Profile.',
    },
};
